import { FeedItemSchema, type FeedItem } from "@scrile/contracts";
import { readItem, removeItem, writeItem } from "../lib/safeStorage";

/**
 * Переписка в sessionStorage вкладки: последний seq и уже полученные элементы.
 * Обновление страницы (или возврат из оплаты) показывает переписку сразу и спрашивает
 * ленту с того места, где остановились, а не с нуля.
 *
 * Только sessionStorage и только удобство: приватный Safari бросает на обращении,
 * переполнение — тоже; тогда лента просто грузится с начала (safeStorage глотает ошибки).
 * Ключ — подписчик и герой: другой аккаунт в той же вкладке чужого не увидит.
 *
 * Кеш может разойтись с сервером (переписку удалили по запросу о данных, база стенда
 * пересоздана): тогда элемент с последним seq придёт не таким или не придёт вовсе —
 * это проверяет cacheMatches, и кеш выбрасывается.
 */

const VERSION = 2;
/** Значительно короче серверных семи дней; не оставляем старые URL в долгой вкладке. */
export const FEED_CACHE_TTL_MS = 12 * 60 * 60_000;
/** Сколько элементов держать: подписанные ссылки живут неделю, а место — 5 МБ на всё. */
export const CACHE_MAX_ITEMS = 400;
const MAX_BYTES = 1_500_000;

export interface FeedCache {
  savedAt: number;
  lastSeq: number;
  items: FeedItem[];
}

export function cacheKey(userId: string, heroSlug: string): string {
  return `scrile.feed.v${VERSION}:${userId}:${heroSlug}`;
}

interface Stored {
  v: number;
  lastSeq: number;
  items: unknown[];
  savedAt: number;
}

function isStored(value: unknown): value is Stored {
  return (
    typeof value === "object" &&
    value !== null &&
    (value as Stored).v === VERSION &&
    Number.isFinite((value as Stored).savedAt) &&
    Number.isSafeInteger((value as Stored).lastSeq) &&
    (value as Stored).lastSeq >= 0 &&
    Array.isArray((value as Stored).items)
  );
}

export function loadFeedCache(key: string, now = Date.now()): FeedCache | null {
  const raw = readItem("session", key);
  if (!raw) return null;
  try {
    const value: unknown = JSON.parse(raw);
    if (!isStored(value) || now - value.savedAt >= FEED_CACHE_TTL_MS || value.savedAt > now) return null;
    const items: FeedItem[] = [];
    for (const entry of value.items) {
      const parsed = FeedItemSchema.safeParse(entry);
      // Один кривой элемент — весь кеш под подозрением: дешевле загрузить заново
      if (!parsed.success || !Number.isFinite(Date.parse(parsed.data.at)) || !Number.isSafeInteger(parsed.data.seq)) return null;
      if (items.length > 0 && parsed.data.seq <= items.at(-1)!.seq) return null;
      items.push(parsed.data);
    }
    const last = items.at(-1)?.seq ?? 0;
    // Курсор не может отставать от элементов и не должен убегать от них: иначе дыра
    if (value.lastSeq < last) return null;
    return { lastSeq: value.lastSeq, items, savedAt: value.savedAt };
  } catch {
    return null;
  }
}

export function saveFeedCache(key: string, cache: Omit<FeedCache, "savedAt"> & { savedAt?: number }): void {
  // Длинная переписка в кеш не ложится целиком, а кусок без начала показал бы дыру:
  // такую просто грузим с начала, как без кеша
  if (cache.items.length > CACHE_MAX_ITEMS) {
    removeItem("session", key);
    return;
  }
  const payload = JSON.stringify({ v: VERSION, lastSeq: cache.lastSeq, items: cache.items, savedAt: cache.savedAt ?? Date.now() });
  if (payload.length > MAX_BYTES) {
    removeItem("session", key);
    return;
  }
  writeItem("session", key, payload);
}

export function clearFeedCache(key: string): void {
  removeItem("session", key);
}

/**
 * Сошёлся ли кеш с сервером. Первая страница спрашивается с after = lastSeq − 1: в ней
 * должен быть элемент с последним seq кеша, тот же по виду и времени. Нет его — лента на
 * сервере другая (или элемент больше не виден), и кеш неправда.
 */
export function cacheMatches(cache: Pick<FeedCache, "lastSeq" | "items">, firstPage: readonly FeedItem[]): boolean {
  if (cache.lastSeq === 0) return true;
  const cached = cache.items.find((item) => item.seq === cache.lastSeq);
  const fresh = firstPage.find((item) => item.seq === cache.lastSeq);
  if (!fresh) return false;
  if (!cached) return true;
  return cached.kind === fresh.kind && cached.at === fresh.at && cached.from === fresh.from;
}
